/**
 * The card on the net in further languages (F28, docs/15-getraenkekarte.md, Karte in weiteren
 * Sprachen), as SpeiseKarte has it (S36): the house switches languages on; the page comes in the
 * language of the link or of the guest's browser, German where a text has no translation.
 *
 * What stands on the card in another language, in this order:
 *   - the page's own words (WEB_WORDS) and the official names of the 14 allergens (LMIV annex II,
 *     word for word as SpeiseKarte took them from EUR-Lex): fixed, no field;
 *   - the house's own translation of a text (its dictionary, `card`/`phrases`), always first;
 *   - else the base vocabulary (common words of a drinks card), the default words of the additives
 *     (not set by the EU, so the house may overwrite them) or the country's name in the
 *     language (Intl): looked up each time, never copied into the dictionary;
 *   - else the German text.
 * Names of drinks stay as they are (Rioja Crianza, Estrella Galicia) unless the house wrote its own
 * translation of one; a wine's name never changes. Producers, regions, grapes and quality levels are
 * names too and stay.
 */
import { ADDITIVES, ALLERGEN_NAMES, allergenId } from "./drinks.ts";
import type { CardPart } from "./drinks-card.ts";
import { CARD_LANGS, WEB_WORDS, baseCardPhrase, isCardLang, officialAllergenIn, phraseKey } from "./card-lang-tables.ts";
import type { CardLang, WebWords } from "./card-lang-tables.ts";

// The fixed tables from household-kit (see card-lang-tables.ts).
export * from "./card-lang-tables.ts";

/** The languages switched on, from the field `languages` of `card`/`web`, in their fixed order. */
export const cardLangsOf = (v: unknown): CardLang[] => (Array.isArray(v) ? CARD_LANGS.filter((l) => v.includes(l)) : []);

/** Whether a German allergen on the card has an official name: one of the 14 under its usual name. */
const officialAllergen = (name: string): string | null => {
  const id = allergenId(name);
  return id && ALLERGEN_NAMES[id] === name ? id : null;
};

/** Country names a cellar writes that the browser's list calls otherwise. */
const COUNTRY_ALIASES: Record<string, string> = { USA: "US", "Vereinigte Staaten": "US", England: "GB", Schottland: "GB", Großbritannien: "GB", Holland: "NL", Tschechei: "CZ" };
let countryCodes: Map<string, string> | null = null;

/** The code of a country by its German name („Frankreich“ → FR), or null. */
export function countryCode(name: string): string | null {
  if (!countryCodes) {
    countryCodes = new Map(Object.entries(COUNTRY_ALIASES));
    try {
      const de = new Intl.DisplayNames(["de"], { type: "region", fallback: "none" });
      const A = 65;
      for (let i = 0; i < 26; i++)
        for (let j = 0; j < 26; j++) {
          const code = String.fromCharCode(A + i, A + j);
          const n = de.of(code);
          if (n && n !== code && !countryCodes.has(n)) countryCodes.set(n, code);
        }
    } catch {
      // Without the names of the regions the countries stay German.
    }
  }
  return countryCodes.get(phraseKey(name)) ?? null;
}

/** A country's name in a language, or null when it is no country the list knows. */
export function countryIn(name: string, lang: CardLang): string | null {
  const code = countryCode(name);
  if (!code) return null;
  try {
    return new Intl.DisplayNames([lang], { type: "region", fallback: "none" }).of(code) ?? null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// The house's dictionary
// ---------------------------------------------------------------------------

/**
 * The house's own translations, as master data `card`/`phrases`: one field per language and German
 * text, so two devices that translate different texts never overwrite each other. A field's name is
 * the language and a fingerprint of the German text („en.3f…“; names of fields are short), its value
 * the German text and the translation (`{ de, text }`); an empty `text` takes a translation away.
 */
export type CardPhrases = Partial<Record<CardLang, Record<string, string>>>;

export const PHRASES_ID = "phrases";

export const PHRASE_LIMITS = { key: 2000, text: 2000, count: 4000 } as const;

/** 64 bits of FNV-1a in two halves: enough that two texts of one house never share a field. */
function fingerprint(s: string): string {
  let a = 0x811c9dc5;
  let b = 0xcbf29ce4;
  for (const ch of s) {
    const c = ch.codePointAt(0)!;
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ c, 0x01000197) >>> 0;
  }
  return a.toString(16).padStart(8, "0") + b.toString(16).padStart(8, "0");
}

export const phraseField = (lang: CardLang, text: string) => `${lang}.${fingerprint(phraseKey(text))}`;

/** The change that sets a translation („“ takes it away), as fields of `card`/`phrases`. */
export const phraseChange = (lang: CardLang, text: string, translation: string): Record<string, { de: string; text: string }> => ({
  [phraseField(lang, text)]: { de: phraseKey(text).slice(0, PHRASE_LIMITS.key), text: phraseKey(translation).slice(0, PHRASE_LIMITS.text) },
});

/** The dictionary from the fields of `card`/`phrases`. */
export function cardPhrasesOf(fields: Record<string, unknown> | null | undefined): CardPhrases {
  const out: CardPhrases = {};
  for (const [field, value] of Object.entries(fields ?? {})) {
    const m = /^([a-z]{2})\.[0-9a-f]{16}$/.exec(field);
    const v = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
    if (!m || !isCardLang(m[1]) || typeof v.de !== "string" || typeof v.text !== "string" || v.text.trim() === "") continue;
    (out[m[1]] ??= {})[phraseKey(v.de)] = phraseKey(v.text).slice(0, PHRASE_LIMITS.text);
  }
  return out;
}

/** Whether a text has words to translate, not only numbers, amounts and units („0,75 l“, „5 cl“, „1/8 l“). */
export function hasWords(s: string): boolean {
  const rest = phraseKey(s)
    .replace(/(^|[\s\d/])(l|cl|dl|ml|g|kg|vol)\.?(?=$|[\s/|,;)%])/gi, "$1")
    .replace(/[^\p{L}]/gu, "");
  return rest.length >= 2;
}

/** Where a translation comes from: the house, the base vocabulary or the list of countries. */
export type FoundPhrase = { text: string; by: "own" | "base" };

/** A text's translation: the house's own always first, then the base vocabulary or the country's name. */
export function phraseOf(text: string, lang: CardLang, own: Readonly<Record<string, string>>): FoundPhrase | undefined {
  const key = phraseKey(text);
  if (own[key]) return { text: own[key]!, by: "own" };
  const base = baseCardPhrase(key, lang) ?? countryIn(key, lang);
  return base ? { text: base, by: "base" } : undefined;
}

/** The texts of a language a page needs: words, allergens, additives and the house's phrases. */
export interface CardTranslator {
  lang: CardLang | "de";
  words: WebWords;
  /** A heading, a note, a card text, a word: translated, else German. */
  text(s: string): string;
  /** A drink's name: only the house's own translation. */
  name(s: string): string;
  allergen(s: string): string;
  additive(n: number): string;
  /** A line's size: its words translated, the amounts as they are („Bottle 0,33 l“). */
  size(s: string): string;
}

const firstLower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

export function cardTranslator(lang: CardLang | "de", own: Readonly<Record<string, string>> = {}): CardTranslator {
  if (lang === "de")
    return { lang, words: WEB_WORDS.de, text: (s) => s, name: (s) => s, allergen: (s) => s, additive: (n) => ADDITIVES[n - 1] ?? "", size: (s) => s };
  const text = (s: string) => (s && hasWords(s) ? (phraseOf(s, lang, own)?.text ?? s) : s);
  return {
    lang,
    words: WEB_WORDS[lang],
    text,
    name: (s) => own[phraseKey(s)] || s,
    // In a line of facts: „contains sulphur dioxide and sulphites“.
    allergen: (s) => {
      const id = officialAllergen(s);
      const official = id ? officialAllergenIn(id, lang) : undefined;
      return official ? firstLower(official) : text(s);
    },
    // A default like the base vocabulary: the house's own word first (not fixed by the EU, Martin).
    additive: (n) => text(ADDITIVES[n - 1] ?? ""),
    // The words of a size one by one („Flasche“, „Dose“, „Schoppen“); „Magnum“ and amounts stay.
    size: (s) => s.replace(/\p{L}[\p{L}-]*(?:\s+\p{L}[\p{L}-]*)*/gu, (m) => (hasWords(m) ? text(m) : m)),
  };
}

// ---------------------------------------------------------------------------
// The texts of a card
// ---------------------------------------------------------------------------

/** Where a text of the card comes from, as the page of translations groups them. */
export type PhraseOrigin = "heading" | "text" | "name" | "word";
export const PHRASE_ORIGINS: Record<PhraseOrigin, string> = { heading: "Überschriften", text: "Kartentexte", name: "Namen", word: "Weitere Wörter" };

/**
 * Every text of the card that a translation could change, each once, in the order of the card: the
 * titles of parts, sections and groups, notes, the card texts of the drinks, the names of drinks that
 * are not wine (they stay unless the house translates them), words in the facts and the sizes. Not
 * listed: names of wines, producers, regions, grapes, quality levels (names), the allergens with
 * their official names (fixed). The additives of the footnotes are listed: their words are a default.
 */
export function cardPhrases(parts: readonly CardPart[]): { text: string; origin: PhraseOrigin }[] {
  const seen = new Set<string>();
  const out: { text: string; origin: PhraseOrigin }[] = [];
  const add = (origin: PhraseOrigin, ...texts: (string | null | undefined)[]) => {
    for (const t of texts) {
      if (!t || !hasWords(t)) continue;
      const key = phraseKey(t);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ text: key, origin });
    }
  };
  for (const p of parts) {
    add("heading", p.title);
    add("word", p.note);
    for (const s of p.sections) {
      add("heading", s.title);
      for (const g of s.groups) {
        add("heading", g.title);
        for (const e of g.entries) {
          if (e.plain) add("name", e.wine);
          add("text", e.text);
          add("word", ...(e.extra ?? []), e.taste && e.taste !== "trocken" ? e.taste : null, e.country);
          add("word", ...(e.allergens ?? []).filter((a) => !officialAllergen(a)), ...(e.notes ?? []).map((n) => ADDITIVES[n - 1]));
          for (const l of e.lines) for (const m of l.size.match(/\p{L}[\p{L}-]*(?:\s+\p{L}[\p{L}-]*)*/gu) ?? []) add("word", m);
        }
      }
    }
  }
  return out;
}

/**
 * What „Veröffentlichen“ freezes per language: the house's own translations of the texts on the card,
 * nothing of the base vocabulary (it is looked up when the page is made). A language switched on
 * without any translation of its own is there with an empty dictionary.
 */
export function frozenTexts(parts: readonly CardPart[], langs: readonly CardLang[], phrases: CardPhrases): Partial<Record<CardLang, Record<string, string>>> {
  const texts = cardPhrases(parts).map((p) => p.text);
  const out: Partial<Record<CardLang, Record<string, string>>> = {};
  for (const lang of langs) {
    const own = phrases[lang] ?? {};
    out[lang] = Object.fromEntries(texts.filter((t) => own[t]).map((t) => [t, own[t]!]));
  }
  return out;
}
